import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import {
  DEFAULT_TABLE_PAGE_SIZE,
  FaroTable,
} from "@components/common/faro-table/faro-table";
import {
  creditTransactionsSelector,
  fetchingTransactionsFlagsSelector,
  transactionPaginationSelector,
} from "@store/credit-transactions/credit-transactions-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { TransactionsHeaders } from "@components/table/transactions/transactions-table-utils";
import { getTransactionsColumns } from "@components/table/transactions/transactions-columns";
import { useDateTime } from "@hooks/use-date-time";
import { useMembers } from "@hooks/use-members";
import { Box } from "@mui/material";
import { projectsMapByIdSelector } from "@store/projects/projects-selector";
import { useCallback, useMemo, useState } from "react";
import { getMembersMapById } from "@utils/member-utils";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { TableType } from "@utils/track-event/track-event-list";
import { fetchCreditTransactions } from "@store/credit-transactions/credit-transactions-thunk";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useActivityApiClient } from "@api/activity-api/use-activity-api-client";
import { EmptyIcon } from "@flat-ui";

/** List of required columns in the transactions table */
const requiredColumns = [
  TransactionsHeaders.date,
  TransactionsHeaders.user,
  TransactionsHeaders.description,
  TransactionsHeaders.project,
  TransactionsHeaders.credits,
];

/** Renders the table for transactions */
export function TransactionsTable(): JSX.Element {
  const transactions = useAppSelector(creditTransactionsSelector);
  const pagination = useAppSelector(transactionPaginationSelector);
  const { status } = useAppSelector(fetchingTransactionsFlagsSelector);
  const { formatDate } = useDateTime();
  const projectsMap = useAppSelector(projectsMapByIdSelector);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const activityApiClient = useActivityApiClient({
    companyId,
    activity: { companyId },
  });

  const { companyMembers } = useMembers();
  const membersMap = getMembersMapById(companyMembers);

  const [page, setPage] = useState<number>(0);
  const [isFetchingMoreTransactions, setIsFetchingMoreTransactions] =
    useState<boolean>(false);

  const paginatedTransactions = useMemo(() => {
    const start = page * DEFAULT_TABLE_PAGE_SIZE;
    return transactions.slice(start, start + DEFAULT_TABLE_PAGE_SIZE);
  }, [transactions, page]);

  const transactionColumns = useMemo(() => {
    return requiredColumns.map(
      (requiredColumn) =>
        getTransactionsColumns({
          formatDate,
          membersMap,
          projectsMap,
        })[requiredColumn]
    );
  }, [formatDate, membersMap, projectsMap]);

  /**
   * Fetches the next page of transactions if the after is defined
   */
  const loadMoreTransactions = useCallback(async (): Promise<void> => {
    if (pagination.after) {
      setIsFetchingMoreTransactions(true);

      await dispatch(
        fetchCreditTransactions({
          activityApiClient,
          after: pagination.after,
        })
      );

      setIsFetchingMoreTransactions(false);
    }
  }, [activityApiClient, dispatch, pagination.after]);

  // Callback fired when the user navigates to the next or previous pages in the table.
  const handlePageChange = useCallback(
    (newPage: number) => {
      // Fetch the next page of projects if available
      if (newPage > page && pagination.after) {
        void loadMoreTransactions();
      }

      setPage(newPage);
    },
    [loadMoreTransactions, page, pagination.after]
  );

  if (transactions.length === 0 && status === "succeeded") {
    return (
      <EmptyPage
        title="No transactions to show"
        subtitle="It looks like there are no transactions during this period."
        icon={EmptyIcon}
      />
    );
  }

  return (
    <Box
      sx={{
        marginTop: SPACE_ELEMENTS_OF_MODAL,
        overflow: "hidden",
      }}
    >
      <FaroTable
        entities={paginatedTransactions}
        uniqueIdKey={"id"}
        isLoading={status === "pending" || isFetchingMoreTransactions}
        shouldHideCheckbox={true}
        columns={transactionColumns}
        rowHeight={70}
        totalCount={pagination.totalCount}
        currentPage={page}
        onPageChange={handlePageChange}
        tableType={TableType.creditTransactions}
      />
    </Box>
  );
}
